import React, { useState } from "react";
import { CheckCircle2, RotateCcw, Trash2, History, Send } from "lucide-react";
import { COMMENT_TOOLS } from "./CommentToolbar";

const ICONS = Object.fromEntries(COMMENT_TOOLS);

function CommentItem({ c, cm, active, onSelect, dateFmt, t, pageLabel }) {
    const [reply, setReply] = useState("");
    const [showHist, setShowHist] = useState(false);
    const Icon = ICONS[c.type];
    const send = () => { const v = reply.trim(); if (!v) return; cm.reply(c.id, v); setReply(""); };
    return (
        <div data-testid={`comment-item-${c.id}`} onClick={() => onSelect(c)}
            className={`rounded-lg border p-3 cursor-pointer transition-colors ${active ? "border-blue-500/70 bg-blue-600/10" : "border-[#1e293b] bg-[#0b0f17] hover:border-[#334155]"} ${c.resolved ? "opacity-70" : ""}`}>
            <div className="flex items-start gap-2">
                <span className="mt-0.5 w-6 h-6 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: `${c.style?.color || "#ef4444"}33`, color: c.style?.color }}>
                    {Icon && <Icon className="w-3.5 h-3.5" />}
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold truncate">{c.author?.name}</span>
                        <span className="text-[10px] font-mono text-slate-500 shrink-0">{pageLabel}</span>
                    </div>
                    <div className="text-[11px] font-mono text-slate-500">{dateFmt(c.created_at)} · {t(`cm_${c.type}`)}</div>
                    {c.text && <p className="text-sm text-slate-200 mt-1 whitespace-pre-wrap break-words">{c.text}</p>}
                    {c.resolved && <div className="text-[11px] font-mono text-emerald-400 mt-1">✓ {t("cm_resolved_by", { n: c.resolved_by?.name || "" })}</div>}
                </div>
            </div>
            {active && (
                <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
                    {(c.replies || []).map((r) => (
                        <div key={r.id} data-testid={`comment-reply-${r.id}`} className="ml-8 border-l-2 border-[#1e293b] pl-2.5 group">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold">{r.author?.name} <span className="font-mono font-normal text-slate-500">· {dateFmt(r.created_at)}</span></span>
                                {cm.canEditReply(r) && (
                                    <button data-testid={`comment-reply-delete-${r.id}`} onClick={() => cm.removeReply(c.id, r.id)}
                                        className="text-slate-600 hover:text-rose-400 opacity-0 group-hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
                                )}
                            </div>
                            <p className="text-sm text-slate-300 whitespace-pre-wrap break-words">{r.text}</p>
                        </div>
                    ))}
                    <div className="ml-8 flex gap-1.5">
                        <input data-testid={`comment-reply-input-${c.id}`} value={reply} onChange={(e) => setReply(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder={t("cm_reply_ph")}
                            className="flex-1 min-w-0 bg-[#111827] border border-[#1e293b] rounded-md text-sm px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500/60" />
                        <button data-testid={`comment-reply-send-${c.id}`} onClick={send} className="h-7 w-7 flex items-center justify-center rounded-md bg-blue-600 hover:bg-blue-500"><Send className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="ml-8 flex items-center gap-1 text-xs">
                        <button data-testid={`comment-resolve-${c.id}`} onClick={() => cm.update(c.id, { resolved: !c.resolved })}
                            className="flex items-center gap-1 px-2 py-1 rounded hover:bg-[#1e293b] text-emerald-400">
                            {c.resolved ? <><RotateCcw className="w-3 h-3" /> {t("cm_reopen")}</> : <><CheckCircle2 className="w-3 h-3" /> {t("cm_resolve")}</>}
                        </button>
                        <button data-testid={`comment-history-toggle-${c.id}`} onClick={() => setShowHist((s) => !s)}
                            className="flex items-center gap-1 px-2 py-1 rounded hover:bg-[#1e293b] text-slate-400"><History className="w-3 h-3" /> {t("cm_history")}</button>
                        {cm.canEdit(c) && (
                            <button data-testid={`comment-delete-${c.id}`} onClick={() => cm.remove(c.id)}
                                className="ml-auto flex items-center gap-1 px-2 py-1 rounded hover:bg-rose-500/10 text-rose-400"><Trash2 className="w-3 h-3" /> {t("cm_delete")}</button>
                        )}
                    </div>
                    {showHist && (
                        <ul data-testid={`comment-history-${c.id}`} className="ml-8 space-y-0.5 text-[11px] font-mono text-slate-400">
                            {(c.history || []).map((h, i) => <li key={i}>{dateFmt(h.ts)} · {h.user_name} · {t(`cm_ev_${h.action}`)}</li>)}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

export function CommentsPanel({ comments, cm, selectedId, onSelect, dateFmt, t, pageNames }) {
    const [filter, setFilter] = useState("all");
    const list = comments.filter((c) => (filter === "all" ? true : filter === "open" ? !c.resolved : c.resolved))
        .sort((a, b) => a.page - b.page || a.created_at.localeCompare(b.created_at));
    const tabs = [["all", t("cm_all"), comments.length], ["open", t("cm_open"), comments.filter((c) => !c.resolved).length], ["resolved", t("cm_resolved"), comments.filter((c) => c.resolved).length]];
    return (
        <div data-testid="comments-panel" className="h-full flex flex-col">
            <div className="flex gap-1 px-3 py-2 border-b border-[#1e293b]">
                {tabs.map(([k, label, n]) => (
                    <button key={k} data-testid={`comments-filter-${k}`} onClick={() => setFilter(k)}
                        className={`px-2.5 py-1 rounded-md text-xs font-mono transition-colors ${filter === k ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-[#182232]"}`}>
                        {label} <span className="opacity-70">{n}</span>
                    </button>
                ))}
            </div>
            <div className="flex-1 overflow-auto p-3 space-y-2">
                {list.length === 0 && <p data-testid="comments-empty" className="text-sm text-slate-500 text-center py-10">{t("cm_empty")}</p>}
                {list.map((c) => (
                    <CommentItem key={c.id} c={c} cm={cm} active={c.id === selectedId} onSelect={onSelect} dateFmt={dateFmt} t={t}
                        pageLabel={(pageNames || {})[c.page] || t("pv_page", { n: c.page })} />
                ))}
            </div>
        </div>
    );
}
